import { useStore } from '@nanostores/solid'
import { createEffect, createSignal, Show, type JSX } from 'solid-js'
import cakeboxLargeSvg from '~/assets/figma/cake-box-figma.svg'
import checkedPurpleSvg from '~/assets/figma/checked-purple.svg'
import missedBoxSvg from '~/assets/figma/missed-box.svg'
import readyIconSvg from '~/assets/figma/ready-icon-new.svg'
import { Spinner } from '~/components/spinner'
import { m } from '~/paraglide/messages'
import { $pageRevealed } from '~/store/page-revealed'
import { $isInSubscription, $serviceLevelReady } from '~/store/service-level'

const CARD_RADIAL_BG =
  '[background-image:radial-gradient(161.72%_161.72%_at_52.56%_0%,#FFF_0%,#EBE0CC_100%)]'

export function CardShell(props: {
  children: JSX.Element
  variant: 'today' | 'other'
}) {
  return (
    <article
      class={`relative flex w-[clamp(280px,calc(100vw-72px),350px)] shrink-0 flex-col items-center gap-4 rounded-[2rem] border-2 border-white/50 p-4 backdrop-blur-[20px] ${CARD_RADIAL_BG}`}
      data-variant={props.variant}
    >
      {props.children}
    </article>
  )
}

export function CardHeader(props: {
  day: number
  isToday?: boolean
  countdown: string
  onReturnToToday?: () => void
}) {
  return (
    <div class="flex w-full items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold leading-5 text-bakery-brown">
          {m.which_day({ day_number: String(props.day) })}
        </span>
        <Show
          when={props.isToday}
          fallback={
            <Show when={props.onReturnToToday}>
              <button
                type="button"
                onClick={props.onReturnToToday}
                aria-label={m.today_tag()}
                class="flex h-7 items-center gap-1 rounded-[13px] border border-bakery-cream px-3 py-0.5 text-bakery-brown-soft transition-transform duration-150 active:scale-[0.97]"
              >
                <span class="text-sm font-semibold leading-5">
                  {m.today_tag()}
                </span>
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  class="size-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M9 14 4 9l5-5" />
                  <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
                </svg>
              </button>
            </Show>
          }
        >
          <span class="flex h-7 items-center rounded-[13px] border border-bakery-cream px-3 py-0.5 text-sm font-semibold leading-5 text-bakery-brown-soft">
            {m.today_tag()}
          </span>
        </Show>
      </div>
      <div class="flex items-center gap-1 text-bakery-brown-soft">
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" class="size-4">
          <path
            d="M14.6667 8C14.6667 11.68 11.68 14.6667 8 14.6667C4.32 14.6667 1.33333 11.68 1.33333 8C1.33333 4.32 4.32 1.33333 8 1.33333C11.68 1.33333 14.6667 4.32 14.6667 8Z"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M10.4733 10.12L8.40667 8.88667C8.04667 8.67333 7.75333 8.16 7.75333 7.74V5.00667"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="text-xs leading-4">{props.countdown}</span>
      </div>
    </div>
  )
}

export function ProgressCircle(props: {
  progress: number
  goal: number
  state: 'in-progress' | 'complete' | 'empty'
  hideValue?: boolean
}) {
  const pct = () => Math.max(0, Math.min(1, props.progress / props.goal))
  const radius = 49.72
  const strokeWidth = 10.55
  const circumference = 2 * Math.PI * radius
  const dashOffset = () =>
    props.state === 'complete' ? 0 : circumference * (1 - pct())
  const arcColor = '#6E4619'

  // Shared one-shot arm signal — drives both the arc transition AND the
  // integer counter. Waits for the page disclosure to finish so the
  // animation isn't burned under the opacity-0 wrapper.
  const pageRevealed = useStore($pageRevealed)
  const [armed, setArmed] = createSignal(false)
  createEffect(() => {
    if (!pageRevealed() || armed()) return
    let h2 = 0
    const h1 = requestAnimationFrame(() => {
      h2 = requestAnimationFrame(() => setArmed(true))
    })
    return () => {
      cancelAnimationFrame(h1)
      if (h2) cancelAnimationFrame(h2)
    }
  })

  // Integer counter tweened in JS (`@property` + `counter()` works on
  // Chrome but Safari doesn't recompute `counter-reset` while the registered
  // integer animates, so the number stays at 0). Each frame is one Solid
  // signal update — still very cheap; only renders this one span.
  const [displayedProgress, setDisplayedProgress] = createSignal(0)
  createEffect(() => {
    if (!armed()) {
      setDisplayedProgress(0)
      return
    }
    const target = props.progress
    if (target <= 0) {
      setDisplayedProgress(0)
      return
    }
    const start = performance.now()
    const duration = 600
    let raf = requestAnimationFrame(function tick(now) {
      const t = Math.min(1, (now - start) / duration)
      const eased = t * t * t // ease-in cubic
      setDisplayedProgress(Math.round(target * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
    })
    return () => cancelAnimationFrame(raf)
  })

  return (
    <div class="relative size-[110px] shrink-0">
      <svg viewBox="0 0 110 110" class="size-full -rotate-90">
        <circle
          cx="55"
          cy="55"
          r={radius}
          stroke="rgba(0,0,0,0.1)"
          stroke-width={strokeWidth}
          fill="none"
        />
        <Show when={props.state !== 'empty'}>
          <ProgressArc
            armed={armed()}
            radius={radius}
            strokeWidth={strokeWidth}
            circumference={circumference}
            color={arcColor}
            dashOffset={dashOffset()}
          />
        </Show>
      </svg>
      <div
        class="absolute inset-0 flex flex-col items-center justify-center text-center"
        classList={{
          'text-bakery-brown': props.state !== 'empty',
          'text-bakery-muted': props.state === 'empty',
        }}
      >
        <Show
          when={!props.hideValue}
          fallback={
            <span class="text-[14.6px] tabular-nums leading-[20px]">60</span>
          }
        >
          <div class="flex items-baseline">
            <span class="text-[22px] font-bold tabular-nums leading-[31px]">
              {displayedProgress()}
            </span>
            <span class="text-[14.6px] tabular-nums leading-5">
              /{props.goal}
            </span>
          </div>
        </Show>
        <span class="text-[12.8px] leading-[18px]">{m.min()}</span>
      </div>
    </div>
  )
}

/** Brown progress arc. Receives the shared `armed` flag from
 *  ProgressCircle so the arc and the integer counter stay in lock-step. */
function ProgressArc(props: {
  armed: boolean
  radius: number
  strokeWidth: number
  circumference: number
  color: string
  dashOffset: number
}) {
  return (
    <circle
      cx="55"
      cy="55"
      r={props.radius}
      stroke={props.color}
      stroke-width={props.strokeWidth}
      fill="none"
      stroke-dasharray={String(props.circumference)}
      style={{
        'stroke-dashoffset': String(
          props.armed ? props.dashOffset : props.circumference,
        ),
        transition: 'stroke-dashoffset 600ms ease-in',
      }}
    />
  )
}

export function CtaTapToOpen(props: {
  baking?: boolean
  loading?: boolean
  onClick?: () => void
}) {
  const disabled = () => props.baking === true || props.loading === true
  return (
    <button
      type="button"
      disabled={disabled()}
      onClick={props.onClick}
      class="relative flex h-[52px] w-[162px] items-center justify-end rounded-[16px] border-2 py-4 pl-[60px] pr-3 transition-colors [transition-duration:100ms] max-[421px]:w-full max-[350px]:justify-center max-[350px]:px-0"
      classList={{
        'border-bakery-yellow-glow bg-bakery-yellow opacity-50 cursor-not-allowed':
          disabled(),
        'btn-shimmer border-bakery-yellow-glow bg-bakery-yellow animate-glow active:scale-[0.97] active:bg-[rgba(180,140,8,0.7)] active:border-[#ebac17]':
          !disabled(),
      }}
    >
      <img
        src={cakeboxLargeSvg}
        alt=""
        aria-hidden="true"
        class="pointer-events-none absolute left-[2.5px] top-[calc(50%-10px)] size-[52px] -translate-y-1/2 max-[350px]:left-1/2 max-[350px]:-translate-x-1/2"
      />
      <span class="flex min-w-0 flex-1 items-center justify-center text-bakery-brown max-[350px]:hidden">
        <Show
          when={!props.loading}
          fallback={
            <Spinner variant="circular" class="size-5 text-bakery-brown" />
          }
        >
          <span class="whitespace-nowrap text-center text-[clamp(11px,3.5vw,16px)] font-bold leading-[20px]">
            {m.btn_open()}
          </span>
        </Show>
      </span>
    </button>
  )
}

export function CtaUpgradeToOpen(props: {
  baking?: boolean
  loading?: boolean
  onUpgrade?: () => void
  onClaim?: () => void
}) {
  const isInSubscription = useStore($isInSubscription)
  const serviceLevelReady = useStore($serviceLevelReady)
  const claimDisabled = () => props.baking === true || props.loading === true
  return (
    <Show when={serviceLevelReady()} fallback={<CtaSkeleton />}>
      <Show
        when={isInSubscription()}
        fallback={
          <button
            type="button"
            disabled={props.baking}
            onClick={props.baking ? undefined : props.onUpgrade}
            class="relative flex h-[52px] w-[162px] items-center justify-end rounded-[16px] border-2 py-4 pl-[60px] pr-3 transition-all duration-100 [background-image:linear-gradient(108deg,#c5ff70_13%,#30d3ff_102%)] max-[421px]:w-full max-[350px]:justify-center max-[350px]:px-0"
            classList={{
              'border-[rgba(98,240,98,0.89)] opacity-50 cursor-not-allowed':
                props.baking,
              'border-[rgba(98,240,98,0.89)] active:scale-[0.97] active:border-[rgba(54,138,54,0.89)] active:[background-image:linear-gradient(rgba(0,155,173,0.64),rgba(0,155,173,0.64)),linear-gradient(108deg,#c5ff70_13%,#30d3ff_102%)]':
                !props.baking,
            }}
          >
            <img
              src={cakeboxLargeSvg}
              alt=""
              aria-hidden="true"
              class="pointer-events-none absolute left-[2.5px] top-[calc(50%-10px)] size-[52px] -translate-y-1/2 max-[350px]:left-1/2 max-[350px]:-translate-x-1/2"
            />
            <span class="min-w-0 flex-1 text-bakery-gem-text max-[350px]:hidden">
              <span class="whitespace-nowrap text-center text-[clamp(11px,3.5vw,16px)] font-bold leading-[20px]">
                {m.btn_open()}
              </span>
            </span>
            <PlusBonusTag />
          </button>
        }
      >
        <button
          type="button"
          disabled={claimDisabled()}
          onClick={claimDisabled() ? undefined : props.onClaim}
          class="relative flex h-[52px] w-[162px] items-center justify-end rounded-[16px] border-2 py-4 pl-[60px] pr-3 transition-colors [transition-duration:100ms] max-[421px]:w-full max-[350px]:justify-center max-[350px]:px-0"
          classList={{
            'border-bakery-yellow-glow bg-bakery-yellow opacity-50 cursor-not-allowed':
              claimDisabled(),
            'btn-shimmer border-bakery-yellow-glow bg-bakery-yellow animate-glow active:scale-[0.97] active:bg-[rgba(180,140,8,0.7)] active:border-[#ebac17]':
              !claimDisabled(),
          }}
        >
          <img
            src={cakeboxLargeSvg}
            alt=""
            aria-hidden="true"
            class="pointer-events-none absolute left-[2.5px] top-[calc(50%-10px)] size-[52px] -translate-y-1/2 max-[350px]:left-1/2 max-[350px]:-translate-x-1/2"
          />
          <span class="flex min-w-0 flex-1 items-center justify-center text-bakery-brown max-[350px]:hidden">
            <Show
              when={!props.loading}
              fallback={
                <Spinner variant="circular" class="size-5 text-bakery-brown" />
              }
            >
              <span class="whitespace-nowrap text-center text-[clamp(11px,3.5vw,16px)] font-bold leading-[20px]">
                {m.btn_open()}
              </span>
            </Show>
          </span>
          <PlusBonusTag />
        </button>
      </Show>
    </Show>
  )
}

function PlusBonusTag() {
  return (
    <span class="pointer-events-none absolute -top-[7px] right-2 z-20 inline-flex h-[15px] items-center rounded-[5px] border border-white px-1.5 max-[350px]:-bottom-[7px] max-[350px]:left-1/2 max-[350px]:right-auto max-[350px]:top-auto max-[350px]:-translate-x-1/2">
      <span
        aria-hidden
        class="pointer-events-none absolute inset-0 rounded-[4px] [background-image:linear-gradient(150deg,rgba(98,240,98,0.89)_7%,#01b1e1_89%)]"
      />
      <span
        aria-hidden
        class="pointer-events-none absolute inset-0 rounded-[4px] [box-shadow:inset_0_-2px_5px_0_rgba(255,255,12,0.41)]"
      />
      <span class="relative whitespace-nowrap text-[8px] font-bold leading-[15px] tracking-[0.48px] text-white [font-feature-settings:'liga'_off,'clig'_off]">
        {m.plus_bonus_tag()}
      </span>
    </span>
  )
}

export function CtaOpened() {
  return (
    <div class="relative flex h-[52px] w-[154px] items-center overflow-clip rounded-[16px] bg-bakery-cream p-4 max-[421px]:w-full max-[350px]:justify-center max-[350px]:p-0">
      <div class="flex min-w-0 flex-1 items-center gap-1 pl-3 max-[350px]:flex-none max-[350px]:pl-0">
        <img
          src={checkedPurpleSvg}
          alt=""
          aria-hidden="true"
          class="size-6 shrink-0"
        />
        <p class="min-w-0 flex-1 whitespace-nowrap text-center text-[clamp(11px,3.5vw,16px)] font-bold leading-[20px] text-bakery-brown opacity-50 max-[350px]:hidden">
          {m.opened_state()}
        </p>
      </div>
    </div>
  )
}

/** Skeleton placeholder for the Today card CTA slot — reserves the 52×162
 *  pill footprint while the progresses query is still loading so the row
 *  doesn't reflow when real data arrives. No cake-box icon, no border, just
 *  a muted grayscale bar with a centered shimmer text strip. */
export function CtaSkeleton() {
  return (
    <div
      aria-hidden
      class="flex h-[52px] w-[162px] animate-pulse items-center justify-center rounded-[16px] bg-black/10 max-[421px]:w-full"
    >
      <div class="h-3 w-16 rounded-full bg-black/15" />
    </div>
  )
}

export function ReadyIcon() {
  return (
    <span class="block size-[50px] animate-float">
      <img src={readyIconSvg} alt="" aria-hidden="true" class="size-full" />
    </span>
  )
}

export function MissedIcon() {
  return (
    <span class="relative block size-[50px] overflow-clip">
      <img
        src={missedBoxSvg}
        alt=""
        aria-hidden="true"
        class="absolute left-[3.99px] top-[25.11px] h-[18.6px] w-[43.4px]"
      />
      <span class="absolute left-[26.25px] top-[12.5px] -translate-x-1/2 -translate-y-1/2 text-[17.5px] font-bold leading-[25px] text-bakery-brown-soft">
        ?
      </span>
    </span>
  )
}
